import { useGetAccountQuery } from '@/services/accounts';
import { useFetchWaterHeaterAccountPricingMutation } from '@/services/pricing';
import { useGetCurrentDesignQuery } from '@/services/systemDesign';
import {
  Alert,
  Box,
  Button,
  FormikRadioGroup,
  FormikTextInput,
  SimulatedEventPayloadType,
} from '@palmetto/palmetto-components';
import { Field, Form, Formik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SyntheticEvent, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ProgramType } from 'types';

import FormSection from '../Forms/FormSection';
import LoadingOverlay from '../LoadingOverlay';
import { MainContainer } from '../MainContainer';

import { FormResultSection } from './HVACQuoteCalculator.components';
import { calculateWaterHeaterSystemDesignTotalCost } from './WaterHeaterQuoteCalculator.utils';

export const WaterHeaterPricingCalculator = () => {
  const { monthlyPaymentCap = false } = useFlags();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const { data: account } = useGetAccountQuery(id);
  const programType = account?.programType || ProgramType.waterHeater;
  const { data: systemDesign } = useGetCurrentDesignQuery({ id, programType }, { skip: !id || !account });
  const [trigger, { data = [], isLoading, status }] = useFetchWaterHeaterAccountPricingMutation();
  const systemDesignCost = useMemo(() => {
    if (!systemDesign) {
      return 0;
    }
    return calculateWaterHeaterSystemDesignTotalCost(systemDesign);
  }, [systemDesign]);

  const ratesAndTerms = useMemo(() => {
    if (!data || !data.length) {
      return { rates: [], terms: [] };
    }

    const rates = Array.from(new Set(data.map((item: any) => item.escalationRate)))
      .toSorted((a: any, b: any) => a - b)
      .map((rate: any) => ({
        label: `${(rate * 100).toFixed(2)}%`,
        value: rate.toString(),
        id: rate.toString(),
      }));
    const terms = Array.from(new Set(data.map((item: any) => item.term)))
      .toSorted((a: any, b: any) => a - b)
      .map((term: any) => ({
        label: `${term} years`,
        value: term.toString(),
        id: term.toString(),
      }));
    return { rates, terms };
  }, [data]);

  const pricingOptionsLimitedByMonthlyPaymentCap =
    monthlyPaymentCap && data.some((item: any) => item.resultsLimitedByMonthlyPaymentCap);
  const initialValues = {
    totalFinancedAmount: systemDesignCost,
    useCurrentSystemDesign: systemDesign ? 'true' : 'false',
  };

  const handleGetPricing = async (formValues: any) => {
    try {
      await trigger({
        accountId: id,
        totalFinancedAmount:
          formValues.useCurrentSystemDesign === 'false' ? formValues.totalFinancedAmount : undefined,
        useCurrentSystemDesign: formValues.useCurrentSystemDesign === 'true',
      });
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  return (
    <MainContainer>
      <Box position={'relative'}>
        {isLoading && <LoadingOverlay isVisible={true} />}

        <Box as="h2" fontWeight="medium" fontSize="lg" padding={{ base: 'lg lg 0 lg' }}>
          Payment Calculator
          <Box padding={{ base: 'xs 0 0 0' }}>
            {pricingOptionsLimitedByMonthlyPaymentCap && (
              <Alert
                variant="warning"
                message="Pricing options are currently limited based on the credit applicant or applicants (if there are co-applicants) on this account. Additional options may be available by adding a new co-applicant."
              ></Alert>
            )}
            <Formik initialValues={initialValues} onSubmit={handleGetPricing} enableReinitialize={true}>
              {({ setFieldValue, values, isSubmitting }) => (
                <Form>
                  <FormSection
                    title="Total Financed Amount"
                    baseStyle={{
                      alignItems: {
                        base: 'flex-start',
                        tablet: 'flex-start',
                        desktop: 'center',
                      },
                      borderColor: 'transparent',
                      maxWidth: {
                        base: '100',
                        desktop: '100',
                      },
                    }}
                  >
                    <Field
                      name="totalFinancedAmount"
                      id="totalFinancedAmount"
                      component={FormikTextInput}
                      prefix="$"
                      width="2xl"
                      onChange={(event: SimulatedEventPayloadType) => {
                        const value = event.target.value;
                        if (value.endsWith('.')) {
                          setFieldValue(`totalFinancedAmount`, value);
                        } else {
                          const numericValue = Number(value);
                          if (!isNaN(numericValue)) {
                            setFieldValue(`totalFinancedAmount`, value);
                            if (values.useCurrentSystemDesign === 'true' && parseFloat(value) !== systemDesignCost) {
                              setFieldValue('useCurrentSystemDesign', 'false');
                            }
                          }
                        }
                      }}
                      onBlur={(event: SimulatedEventPayloadType) => {
                        const value = parseFloat(event.target.value);
                        setFieldValue(`totalFinancedAmount`, isNaN(value) ? 0 : value);
                      }}
                      onFocus={(event: SimulatedEventPayloadType) => {
                        const value = parseFloat(event.target.value);
                        if (value === 0) {
                          setFieldValue(`totalFinancedAmount`, '');
                        }
                      }}
                    />
                  </FormSection>
                  {systemDesign ? (
                    <FormSection title="Use Current System Design">
                      <Box
                        direction={{
                          base: 'column',
                          tablet: 'row',
                        }}
                        childGap={{ base: 'lg', desktop: 'xl' }}
                        style={{
                          maxWidth: '9.8em',
                        }}
                      >
                        <Field
                          component={FormikRadioGroup}
                          id="useCurrentSystemDesign"
                          name="useCurrentSystemDesign"
                          options={[
                            { id: 'yes', label: 'Yes', value: 'true' },
                            { id: 'no', label: 'No', value: 'false' },
                          ]}
                          onChange={(data: SyntheticEvent<HTMLInputElement>) => {
                            const selectedValue = (data.target as HTMLInputElement).value;
                            if (typeof selectedValue === 'undefined') {
                              return;
                            }
                            if (selectedValue === 'true') {
                              setFieldValue('totalFinancedAmount', systemDesignCost);
                            }
                            setFieldValue('useCurrentSystemDesign', selectedValue);
                          }}
                        />
                      </Box>
                    </FormSection>
                  ) : null}
                  {!data.length && status !== 'uninitialized' && !isLoading ? (
                    <Alert variant="info" message="No pricing options available for this account" />
                  ) : null}
                  {data?.length ? (
                    <>
                      <FormSection title="Term (Contract Length)">
                        <Field
                          component={FormikRadioGroup}
                          id="term"
                          name="term"
                          options={ratesAndTerms.terms}
                          onChange={(data: SyntheticEvent<HTMLInputElement>) => {
                            const selectedValue = (data.target as HTMLInputElement).value;
                            if (typeof selectedValue === 'undefined') {
                              return;
                            }
                            setFieldValue('term', selectedValue);
                          }}
                        />
                      </FormSection>
                      <FormSection title="Select Escalator Rate">
                        <Field
                          component={FormikRadioGroup}
                          id="rate"
                          name="rate"
                          options={ratesAndTerms.rates}
                          onChange={(data: SyntheticEvent<HTMLInputElement>) => {
                            const selectedValue = (data.target as HTMLInputElement).value;

                            if (typeof selectedValue === 'undefined') {
                              return;
                            }
                            setFieldValue('rate', selectedValue);
                          }}
                        />
                      </FormSection>
                    </>
                  ) : null}
                  {data?.length ? <FormResultSection data={data} /> : null}
                  <Box
                    direction={{
                      base: 'column',
                      tablet: 'row',
                    }}
                    alignItems={{
                      base: 'stretch',
                      tablet: 'flex-end',
                    }}
                    justifyContent={{
                      tablet: 'flex-end',
                    }}
                    childGap="sm"
                    style={{ flexShrink: 0 }}
                    padding={{ base: 'lg', desktop: 'lg 0 0 0' }}
                  >
                    <Button
                      variant="secondary"
                      tone="neutral"
                      size="md"
                      onClick={() => {
                        navigate(`/accounts/${id}`);
                      }}
                      isDisabled={isLoading}
                      isLoading={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="md"
                      variant="primary"
                      type="submit"
                      // isDisabled={isLoading || !selectedElement || isDisabled}
                      isLoading={isSubmitting}
                    >
                      Fetch Pricing
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>
        </Box>
      </Box>
    </MainContainer>
  );
};
